import { ConnectivityState, simulateLatency, type ApiContext } from '../shared';
import { STUB_LOCATIONS } from './stubData';
import type {
  Location,
  CreateLocationRequest,
  UpdateLocationRequest,
  ListLocationsParams,
  ListLocationsResponse,
  TestConnectivityResponse,
} from './types';
import { apiClient } from '@admin-ui-root/src/app/lib/apiClient';
import { fileDeliveryApiUrl } from '@admin-ui-root/src/app/pages/FileDelivery/api/api';

export const listLocations = async (ctx: ApiContext, params?: ListLocationsParams): Promise<ListLocationsResponse> => {
  const api = apiClient();

  // Build query parameters
  const searchParams = new URLSearchParams();
  if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
  if (params?.pageToken) searchParams.append('pageToken', params.pageToken);
  if (params?.filter) searchParams.append('filter', params.filter);
  if (params?.orderBy) searchParams.append('orderBy', params.orderBy);

  const queryString = searchParams.toString();
  const url = queryString ? `${fileDeliveryApiUrl('locations')}?${queryString}` : fileDeliveryApiUrl('locations');

  const response = await api
    .get(url, {
      headers: {
        'x-fds-profile': ctx.serviceAccount,
        'x-c2fo-dc': ctx.datacenter,
      },
    })
    .json<unknown>();

  // Handle empty object response when no results are found
  if (!response || typeof response !== 'object' || !('locations' in response)) {
    return {
      locations: [],
      totalSize: 0,
    };
  }

  const typedResponse = response as ListLocationsResponse;
  return {
    locations: typedResponse.locations || [],
    nextPageToken: typedResponse.nextPageToken,
    totalSize: typedResponse.totalSize || 0,
  };
};

export const getLocation = async (ctx: ApiContext, name: string): Promise<Location | null> => {
  const api = apiClient();

  try {
    const response = await api
      .get(fileDeliveryApiUrl(name), {
        headers: {
          'x-fds-profile': ctx.serviceAccount,
          'x-c2fo-dc': ctx.datacenter,
        },
      })
      .json<{ location: Location }>();

    return response.location;
  } catch (error) {
    // Return null if location not found (404) or other errors
    return null;
  }
};

export const createLocation = async (_ctx: ApiContext, _request: CreateLocationRequest): Promise<Location> => {
  // TODO: Replace with real API call when available
  // No-op implementation - returns a mock created location
  await simulateLatency();

  const newLocation: Location = {
    ..._request.location,
    name: `locations/${crypto.randomUUID()}`,
    uid: crypto.randomUUID(),
    storageType: STUB_LOCATIONS[0].storageType,
    connectivityState: ConnectivityState.CONNECTIVITY_STATE_UNTESTED,
    createTime: new Date().toISOString(),
    updateTime: new Date().toISOString(),
  };

  return newLocation;
};

export const updateLocation = async (_ctx: ApiContext, _request: UpdateLocationRequest): Promise<Location> => {
  // TODO: Replace with real API call when available
  // No-op implementation - returns the existing location with updated fields
  await simulateLatency();

  const existing = STUB_LOCATIONS.find((loc) => loc.name === _request.location.name);
  if (!existing) {
    throw new Error(`Location not found: ${_request.location.name}`);
  }

  return {
    ...existing,
    ..._request.location,
    updateTime: new Date().toISOString(),
  };
};

export const deleteLocation = async (_ctx: ApiContext, _name: string, _force?: boolean): Promise<void> => {
  // TODO: Replace with real API call when available
  // No-op implementation
  await simulateLatency();
};

export const testLocationConnectivity = async (_ctx: ApiContext, name: string): Promise<TestConnectivityResponse> => {
  // TODO: Replace with real API call when available
  await simulateLatency(500, 1500);

  const location = STUB_LOCATIONS.find((loc) => loc.name === name || loc.uid === name);

  if (!location) {
    return {
      connectivityState: ConnectivityState.CONNECTIVITY_STATE_UNTESTED,
      message: 'Location not found',
      latency: '0ms',
    };
  }

  return {
    connectivityState: ConnectivityState.CONNECTIVITY_STATE_CONNECTED,
    message: 'Connection test successful',
    latency: '150ms',
  };
};
